import {
  Box,
  Checkbox,
  DatepickerSimple,
  RailzCategory,
  RailzTaxRate,
  RailzTrackingCategory,
  RailzVendor,
  Select,
  TextArea,
  TextField,
  Tooltip,
} from 'vopay';
import { FieldData } from '../../Form/types';
import * as Yup from 'yup';
import styles from './CreateBillPage.module.scss';
import { format } from 'date-fns';
export type LineItem = {
  Description: string;
  Amount: string;
  Quantity: string;
  TaxRateID?: string;
  TaxAmount?: string;
  AccountRefID: string;
  TrackingCategoryRefs: string;
  BillableStatus: boolean;
};

export type InvoiceSubmission = {
  VendorInvoiceNumber: string;
  PostedDate: string;
  DueDate: string;
  VendorRefID: string;
  CustomerEmail: string;
  CustomerMailingAddress: string;
  Terms: 'Due on Receipt' | 'Custom' | 'Net 15' | 'Net 30' | 'Net 60' | 'Net 80' | 'Net 90' | '';
  Memo: string;
  Lines: LineItem[];
};

export function buildInvoiceNumberRows(): FieldData<InvoiceSubmission>[] {
  return [
    {
      label: () => '',
      fieldName: 'VendorInvoiceNumber',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: InvoiceSubmission) => values.VendorInvoiceNumber,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Box maxwidth="128px" ai="fe">
            <TextField maxLength={21} variant="outlineShadow" placeholder="" {...fieldData} />
          </Box>
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
  ];
}

export function buildInvoiceNoteRows(): FieldData<InvoiceSubmission>[] {
  return [
    {
      label: () => 'Message',
      fieldName: 'Memo',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: InvoiceSubmission) => values.Memo,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextArea
            maxLength={40000}
            variant="outlinedShadow"
            placeholder="Type your message here"
            height="95px"
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().notRequired(),
    },
  ];
}

export function buildInvoiceFormRows(vendors: RailzVendor[]): FieldData<InvoiceSubmission>[] {
  const termsOptions = [
    { value: 'Due on Receipt', label: 'Due on Receipt' },
    { value: 'Custom', label: 'Custom' },
    { value: 'Net 15', label: 'Net 15' },
    { value: 'Net 30', label: 'Net 30' },
    { value: 'Net 60', label: 'Net 60' },
    { value: 'Net 80', label: 'Net 80' },
    { value: 'Net 90', label: 'Net 90' },
  ];
  return [
    {
      label: () => 'Supplier',
      fieldName: 'VendorRefID',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: InvoiceSubmission) => values.VendorRefID,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (_, args) => {
            const data: RailzVendor = args.data;
            if (!data) return;
            ctx.setFieldValue('CustomerEmail', data.emailAddress || '');
            ctx.setFieldValue(
              'CustomerMailingAddress',
              [
                data.billingAddress?.line1,
                data.billingAddress?.city,
                data.billingAddress?.region,
                data.billingAddress?.postalCode,
                data.billingAddress?.country,
              ]
                .filter(Boolean)
                .join(', '),
            );
          },
        });
        return (
          <Select
            version="2"
            options={vendors}
            optionValue={(vendors) => vendors.id}
            optionLabel={(vendors) =>
              vendors.contactName || `${vendors.contactFirstName || ''} ${vendors.contactLastName || ''}`
            }
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => 'Supplier Email',
      fieldName: 'CustomerEmail',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: InvoiceSubmission) => values.CustomerEmail,

      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" readOnly placeholder="" {...fieldData} />;
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => 'Mailing Address',
      fieldName: 'CustomerMailingAddress',

      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: InvoiceSubmission) => values.CustomerMailingAddress,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" readOnly placeholder="" {...fieldData} />;
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => 'Terms',
      fieldName: 'Terms',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: InvoiceSubmission) => values.Terms,
      tooltip: () => (
        <Tooltip
          information="Terms indicate the number of days a customer has to pay you. You can adjust this before sending the invoice.
"
          position="top"
          width="394px"
        />
      ),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            options={termsOptions}
            optionValue={(customer) => customer.value}
            optionLabel={(customer) => customer.label}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => 'Posted Date',
      fieldName: 'PostedDate',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => {
        const date = new Date(Number(values.PostedDate));
        return format(date, 'yyyy-MM-dd');
      },

      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <DatepickerSimple variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => 'Due Date',
      fieldName: 'DueDate',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => {
        const date = new Date(Number(values.DueDate));
        return format(date, 'yyyy-MM-dd');
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <DatepickerSimple variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
  ];
}

export function buildTableFormRows(
  salesTaxes: RailzTaxRate[],
  trackingCategories: RailzTrackingCategory[],
  categories: RailzCategory[],
  currency: string,
  taxCalculation: 'inclusive' | 'exclusive' | 'outOfScope',
): FieldData<LineItem>[] {
  return [
    {
      label: () => '',
      fieldName: 'AccountRefID',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.AccountRefID,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            options={categories}
            optionValue={(category) => category.id}
            optionLabel={(category) => category.name}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => '',
      fieldName: 'Description',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.Description,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => '',
      fieldName: 'Quantity',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.Amount,

      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="numericWholePositive"
            placeholder=""
            {...fieldData}
            inputClass={styles.quantityField}
          />
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },

    {
      label: () => '',
      fieldName: 'Amount',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.Amount,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="currency"
            placeholder=""
            elementLeft={<div className={styles.currency}>{currency}</div>}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },

    {
      label: () => '',
      fieldName: 'TaxRateID',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.TaxRateID,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            options={salesTaxes}
            optionValue={(tax) => tax.id}
            optionLabel={(tax) => tax.name}
            {...fieldData}
          />
        );
      },
      validate: () => (taxCalculation == 'outOfScope' ? undefined : Yup.mixed().required('This value is required.')),
    },
    {
      label: () => '',
      fieldName: 'BillableStatus',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.BillableStatus,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});
        return (
          <Box flx="col" jc="center" ai="center">
            <Checkbox variant="border" {...fieldData} />
          </Box>
        );
      },
    },

    {
      label: () => '',
      fieldName: 'TrackingCategoryRefs',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.TrackingCategoryRefs,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            options={trackingCategories}
            optionValue={(category) => category.id}
            optionLabel={(category) => category.name}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
  ];
}
